import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Building2, Plus } from "lucide-react";
import { DetailHeader, BackLink } from "@/components/DetailHeader";
import { PropGrid } from "@/components/PropGrid";
import { SectionCard } from "@/components/SectionCard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CustomerStatusBadge, LicenseStatusBadge, TrialBadge } from "@/components/StatusBadges";
import { CreateLicenseDialog } from "@/components/dialogs/CreateLicenseDialog";
import { SpecDialog } from "@/components/dialogs/SpecDialog";
import { AdminInviteDialog } from "@/components/AdminInviteDialog";
import { SpecPhaseBadge } from "@/components/StatusBadges";
import type { AdminInviteIssue } from "@/types";
import { useSpecs } from "@/api/queries/provisioning";
import { useCustomer } from "@/api/queries/customers";
import { useLicenses } from "@/api/queries/licenses";
import { useSuspendCustomer } from "@/api/mutations/customers";
import { relativeTime } from "@/lib/relativeTime";

export function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const customer = useCustomer(id);
  const licenses = useLicenses(id);
  const suspend = useSuspendCustomer();
  const [createLicOpen, setCreateLicOpen] = useState(false);
  // Umgebungen = Kundenspezifikationen dieses Kunden (eine je Zelle) — die Verknuepfung Kunde → Kubernetes.
  const specs = useSpecs({ customerId: id });
  const [createSpecOpen, setCreateSpecOpen] = useState(false);
  // #343 (UC-9): Einladung des Erst-Admins — kommt mit der Antwort des Anlegens, wird genau einmal gezeigt.
  const [invite, setInvite] = useState<AdminInviteIssue | null>(null);

  if (customer.isLoading) {
    return (
      <div className="max-w-6xl">
        <Card>
          <CardContent className="p-6" data-testid="detail-loading">
            Lade…
          </CardContent>
        </Card>
      </div>
    );
  }

  if (customer.isError || !customer.data) {
    return (
      <div className="max-w-6xl space-y-4">
        <BackLink to="/customers" label="Kunden" />
        <Card>
          <CardContent className="p-6 text-danger" data-testid="detail-error">
            Kunde nicht gefunden.
          </CardContent>
        </Card>
      </div>
    );
  }

  const c = customer.data;
  const custLicenses = licenses.data ?? [];

  return (
    <div className="max-w-6xl space-y-5">
      <DetailHeader
        backTo="/customers"
        backLabel="Kunden"
        icon={Building2}
        title={`${c.code} · ${c.name}`}
        badge={<CustomerStatusBadge status={c.status} />}
        sub={c.email}
        actions={
          <Button
            variant={c.status === "suspended" ? "default" : "destructive"}
            disabled={suspend.isPending}
            onClick={() => suspend.mutate({ id: c.id, suspended: c.status !== "suspended" })}
            data-testid="toggle-suspend-customer"
          >
            {c.status === "suspended" ? "Entsperren" : "Sperren"}
          </Button>
        }
      />

      <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
        <SectionCard
          title="Lizenzen"
          description="Alle Lizenzen dieses Kunden"
          noPad
          action={
            <Button size="sm" onClick={() => setCreateLicOpen(true)} data-testid="create-license-for-customer">
              <Plus className="h-4 w-4" /> Lizenz
            </Button>
          }
        >
          {custLicenses.length === 0 ? (
            <div className="p-6 text-center text-sm text-text3" data-testid="customer-licenses-empty">
              Keine Lizenzen.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Edition</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Gueltig bis</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {custLicenses.map((l) => (
                  <TableRow key={l.id} data-clickable="true" onClick={() => navigate(`/licenses/${l.id}`)}>
                    <TableCell>
                      <Link to={`/licenses/${l.id}`} className="text-accent hover:underline">
                        {l.edition}
                      </Link>{" "}
                      {l.trial && <TrialBadge />}
                    </TableCell>
                    <TableCell>
                      <LicenseStatusBadge status={l.status} />
                    </TableCell>
                    <TableCell className="text-text3">{l.validUntil.slice(0, 10)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </SectionCard>

        <PropGrid
          rows={[
            ["Kennung", <span className="mono text-xs" data-testid="customer-code">{c.code}</span>],
            ["Kunden-ID", <span className="mono text-xs">{c.id}</span>],
            ["Email", c.email],
            ["Status", <CustomerStatusBadge status={c.status} />],
            ["Angelegt", relativeTime(c.createdAt)],
          ]}
        />
      </div>

      <SectionCard
        title="Umgebungen"
        description="Wo dieser Kunde laeuft: je Zelle eine Spezifikation, der Zellen-Agent meldet den Stand"
        noPad
        action={
          <Button size="sm" onClick={() => setCreateSpecOpen(true)} data-testid="create-spec-for-customer">
            <Plus className="h-4 w-4" /> Umgebung
          </Button>
        }
      >
        {specs.isError ? (
          <div className="p-6 text-sm text-danger" data-testid="customer-specs-error">Umgebungen nicht abrufbar.</div>
        ) : (specs.data ?? []).length === 0 ? (
          <div className="p-6 text-center text-sm text-text3" data-testid="customer-specs-empty">
            Noch keine Umgebung — «Umgebung» legt den Kunden in einer Zelle an.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Zelle</TableHead>
                <TableHead>Stand</TableHead>
                <TableHead>Adresse</TableHead>
                <TableHead>Version</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(specs.data ?? []).map((s) => (
                <TableRow key={s.id} data-clickable="true" onClick={() => navigate(`/customer-specs/${s.id}`)} data-testid={`customer-spec-${s.id}`}>
                  <TableCell>
                    <Link to={`/customer-specs/${s.id}`} className="text-accent hover:underline">{s.cell}</Link>
                  </TableCell>
                  <TableCell><SpecPhaseBadge phase={s.lastStatus?.phase} removed={s.removed} /></TableCell>
                  <TableCell>
                    {s.host ? (
                      <a href={`https://${s.host}/ui`} target="_blank" rel="noreferrer" className="text-accent hover:underline" onClick={(e) => e.stopPropagation()} data-testid={`customer-spec-link-${s.id}`}>
                        {s.host}
                      </a>
                    ) : (
                      <span className="text-text3">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-text3">v{s.version} · {s.imageTag}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>

      {createSpecOpen && <SpecDialog open={createSpecOpen} onOpenChange={setCreateSpecOpen} presetCustomerId={c.id} onCreated={(created) => setInvite(created.adminInvite)} />}
      <AdminInviteDialog invite={invite} onClose={() => setInvite(null)} />
      {createLicOpen && (
        <CreateLicenseDialog open={createLicOpen} onOpenChange={setCreateLicOpen} presetCustomerId={c.id} />
      )}
    </div>
  );
}
